import { DEFAULT_USER } from "~/constants/user";
import { USER_KIND_CUSTOM, USER_KIND_DART_AI, USER_KIND_USER } from "~/constants/userKinds";
import { isDefined } from "~/shared/common";
import { ExecutionMode, PropertyKind, StatusKind, UserRole, UserStatus } from "~/shared/enums";
import type {
  AgentExtension,
  AgentExtensionUpdate,
  Dartboard,
  Folder,
  Task,
  User,
  UserCreate,
  UserUpdate,
} from "~/shared/types";
import { makeRandomColorHex } from "~/utils/color";
import { makeDuid, unionOfAllSets } from "~/utils/common";
import { agentComparator, userComparator } from "~/utils/comparator";
import { isAgentInactive, isAgentSetupRequired, isInaccessibleAgent, makeDefaultAgentExtension } from "~/utils/user";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";
import { removeFromSorted, setObjectMapAndSorted, updateInSorted } from "./utils";

const SPECIALIST_ROLES = new Set([UserRole.TECHNICAL_ADMIN, UserRole.FINANCIAL_ADMIN]);

export type State = {
  _duidsToUsers: Map<string, User>;
  _sortedUserDuids: string[];
};

type GetUserListOptions = {
  excludeAgents?: boolean;
  excludeNonagents?: boolean;
  includeInactiveAgents?: boolean;
  includeInaccessibleAgents?: boolean;
  includeSpecialists?: boolean;
  includePendingNavigation?: boolean;
};

type GetBuildAgentListOptions = {
  includeInactive?: boolean;
  includeUnavailable?: boolean;
};

export const state: State = {
  _duidsToUsers: new Map(),
  _sortedUserDuids: [],
};

const makeBackendAgentExtension = (agent: AgentExtension | AgentExtensionUpdate): AgentExtensionUpdate => {
  const agentForBackend = { ...agent } as Partial<AgentExtension>;
  delete agentForBackend.localOnline;
  delete agentForBackend.localState;
  delete agentForBackend.defaultKind;
  delete agentForBackend.activeSessions;
  return agentForBackend;
};

const makeBackendUserCreate = (user: User): UserCreate => {
  const userForBackend = { ...user } as UserCreate & Partial<Pick<User, "generatedDescription">>;
  delete userForBackend.generatedDescription;
  return {
    ...userForBackend,
    agent: user.agent ? makeBackendAgentExtension(user.agent) : user.agent,
  };
};

const makeBackendUserUpdate = (update: UserUpdate): UserUpdate => {
  const updateForBackend = { ...update } as UserUpdate & Partial<Pick<User, "generatedDescription">>;
  delete updateForBackend.generatedDescription;
  return {
    ...updateForBackend,
    agent: update.agent ? makeBackendAgentExtension(update.agent) : update.agent,
  };
};

export type Getters = {
  getUserList: (options?: GetUserListOptions) => User[];
  getBuildAgentList: (options?: GetBuildAgentListOptions) => User[];
  getUserByDuid: (duid: string) => User | undefined;
  getUsersByDuidsOrdered: (duids: string[]) => User[];
  getUserByEmail: (email: string) => User | undefined;
  dartAi: User;
  usersLocalAgent: User | undefined;
  getRelevantUsersByTasks: (tasks: Task[], options?: GetUserListOptions) => User[];
  getRelevantUsersByDartboardsOrFolders: (
    dartboardsOrFolders: Dartboard[] | Folder[],
    options?: GetUserListOptions
  ) => User[];
};

export type Actions = {
  updateUser: (update: UserUpdate, options?: { awaitBackend?: boolean }) => Promise<User | undefined>;
  removeUser: (user: User, options?: { awaitBackend?: boolean }) => Promise<void>;
  createAgent: (
    name: string,
    partialAgent?: Partial<User>,
    options?: { awaitBackend?: boolean; duid?: string }
  ) => Promise<User>;
  updateAgent: (
    update: UserUpdate,
    options?: { awaitBackend?: boolean; noBackend?: boolean }
  ) => Promise<User | undefined>;
  deleteAgent: (agent: User, options?: { awaitBackend?: boolean }) => Promise<void>;
  $setUsers: (users: User[]) => void;
  $createOrUpdateUser: (user: User) => void;
  $deleteUser: (user: User) => void;
  $updateAgentStatusNoBackend: (agent: User) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  getUserList() {
    return (options = {}) => {
      const {
        excludeAgents = false,
        excludeNonagents = false,
        includeInactiveAgents = false,
        includeInaccessibleAgents = false,
        includeSpecialists = false,
        includePendingNavigation = false,
      } = options;
      const userIsMemberOrGreater = this.$useUserStore().isRoleGte(UserRole.MEMBER);
      const pageStore = this.$usePageStore();
      const effectiveExcludeAgents = excludeAgents || (!userIsMemberOrGreater && !pageStore.isPublicForm);
      const visibleUserDuids = unionOfAllSets(this.spaceList.map((e) => new Set(e.accessibleByUserDuids)));
      return this._sortedUserDuids
        .map((e) => this._duidsToUsers.get(e))
        .filter(isDefined)
        .filter((e) => {
          const isAgent = e.kind !== USER_KIND_USER;
          return (
            (userIsMemberOrGreater || pageStore.isPublic || visibleUserDuids.has(e.duid) || isAgent) &&
            (isAgent ? !effectiveExcludeAgents : !excludeNonagents) &&
            (!isAgent || includeInactiveAgents || !isAgentInactive(e)) &&
            (includeInaccessibleAgents || !isInaccessibleAgent(e)) &&
            (includeSpecialists || !SPECIALIST_ROLES.has(e.role)) &&
            (includePendingNavigation || !this.pendingNavigationDuids.has(e.duid))
          );
        });
    };
  },
  getBuildAgentList() {
    return ({ includeInactive = false, includeUnavailable = false } = {}) =>
      this.getUserList({ excludeNonagents: true, includeInactiveAgents: includeInactive })
        .filter((agent) => agent.agent?.executionMode === ExecutionMode.LOCAL)
        .filter((agent) => includeUnavailable || !isAgentSetupRequired(agent))
        .sort(agentComparator);
  },
  getUserByDuid() {
    return (duid) => this._duidsToUsers.get(duid);
  },
  getUsersByDuidsOrdered() {
    return (duids) =>
      this.getUserList({ includeInactiveAgents: true, includeInaccessibleAgents: true }).filter((e) =>
        duids.includes(e.duid)
      );
  },
  getUserByEmail() {
    return (email) => this.getUserList().find((e) => e.email === email);
  },
  dartAi() {
    const dartAi = this.getUserList({ excludeNonagents: true, includeInactiveAgents: true }).find(
      (user) => user.kind === USER_KIND_DART_AI
    );
    if (!dartAi) {
      throw new Error("Default chat agent not found.");
    }
    return dartAi;
  },
  usersLocalAgent() {
    const name = this.$useUserStore().localSetupAgentName;
    return this.getBuildAgentList({ includeInactive: true, includeUnavailable: true }).find(
      (agent) => agent.name.trim() === name
    );
  },
  getRelevantUsersByTasks() {
    return (tasks, options) =>
      this.getRelevantUsersByDartboardsOrFolders(
        this.getDartboardsByDuidsOrdered([...new Set(tasks.map((e) => e.dartboardDuid))]),
        options
      );
  },
  getRelevantUsersByDartboardsOrFolders() {
    return (dartboardsOrFolders, options) => {
      const spaceDuids = [...new Set(dartboardsOrFolders.map((e) => e.spaceDuid))];
      const spaces = this.getSpacesByDuidsOrdered(spaceDuids);
      if (spaces.some((e) => e.accessibleByTeam)) {
        return this.getUserList(options);
      }
      const specificAccessorDuids = [
        ...unionOfAllSets(spaces.map((e) => e.accessibleByUserDuids).map((e) => new Set(e))),
      ];
      const isMemberOrGreater = this.$useUserStore().isRoleGte(UserRole.MEMBER);
      const includeAgents = isMemberOrGreater && !options?.excludeAgents;
      const agentDuids = includeAgents ? this.getUserList({ excludeNonagents: true }).map((u) => u.duid) : [];
      return this.getUsersByDuidsOrdered([...new Set([...specificAccessorDuids, ...agentDuids])]);
    };
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async updateUser(update, options = {}) {
    const { awaitBackend = false } = options;
    const user = this.getUserByDuid(update.duid);
    if (!user) {
      return undefined;
    }

    Object.assign(user, update);
    updateInSorted(this._duidsToUsers, this._sortedUserDuids, user.duid, userComparator);

    const backendAction = this.$backend.user.update(update);
    if (awaitBackend) {
      await backendAction;
    }
    return user;
  },
  async removeUser(user, options = {}) {
    const { awaitBackend = false } = options;

    const userPropertyDuids = this.getPropertyByKind(PropertyKind.USER)
      .map((e) => e.duid)
      .concat([
        this.defaultAssigneesProperty.duid,
        this.defaultReviewersProperty.duid,
        this.defaultUpdatedByProperty.duid,
      ]);

    this.$cleanupFormFieldsDefaultsNoBackend([user.duid], userPropertyDuids);
    this.$cleanupLayoutsFiltersNoBackend([user.duid], userPropertyDuids);

    this.$deleteUser(user);

    const backendAction = this.$backendOld.workspace.removeUsers([user.duid]);
    if (awaitBackend) {
      await backendAction;
    }
  },
  async createAgent(name, partialAgent = {}, options = {}) {
    const { awaitBackend = false, duid: providedDuid } = options;

    const startedStatuses = this.getStatusesByKinds([StatusKind.STARTED], this.defaultStatusProperty);
    const canceledStatuses = this.getStatusesByKinds([StatusKind.CANCELED], this.defaultStatusProperty);
    const assignStatusDuid = startedStatuses[0]?.duid ?? null;
    const completeStatusDuid = this.defaultDefaultFinishedStatus.duid;
    const failStatusDuid = canceledStatuses[0]?.duid ?? null;

    const { agent: partialAgentExtension, ...restPartialAgent } = partialAgent;
    const agentExtension: Partial<AgentExtension> = partialAgentExtension ?? {};

    const duid = providedDuid ?? makeDuid();
    const agent: User = {
      ...DEFAULT_USER,
      duid,
      email: `agent+${duid}@dartai.com`,
      kind: USER_KIND_CUSTOM,
      name,
      colorHex: makeRandomColorHex(),
      aiGuidelines: "",
      manualDescription: "",
      generatedDescription: null,
      usingGeneratedDescription: true,
      ...restPartialAgent,
      agent: makeDefaultAgentExtension({
        ...agentExtension,
        assignStatusDuid: agentExtension.assignStatusDuid ?? assignStatusDuid,
        completeStatusDuid: agentExtension.completeStatusDuid ?? completeStatusDuid,
        failStatusDuid: agentExtension.failStatusDuid ?? failStatusDuid,
      }),
    };
    this.$updateAgentStatusNoBackend(agent);

    this.$createOrUpdateUser(agent);

    this.$addEntityDuidPendingCreation(duid);
    const backendAction = (async () => {
      try {
        await this.$backend.user.create(makeBackendUserCreate(agent));
      } finally {
        this.$removeEntityDuidPendingCreation(duid);
      }
    })();
    if (awaitBackend) {
      await backendAction;
    }
    return agent;
  },
  async updateAgent(update, options = {}) {
    const { awaitBackend = false, noBackend = false } = options;
    const agent = this.getUserByDuid(update.duid);
    if (!agent) {
      return undefined;
    }

    const mergedUpdate =
      agent.agent && update.agent
        ? {
            ...update,
            agent: {
              ...agent.agent,
              ...update.agent,
            },
          }
        : update;
    Object.assign(agent, mergedUpdate);
    if (update.status === undefined) {
      this.$updateAgentStatusNoBackend(agent);
    }
    updateInSorted(this._duidsToUsers, this._sortedUserDuids, agent.duid, userComparator);

    if (noBackend) {
      return agent;
    }

    const backendAction = this.$backend.user.update(makeBackendUserUpdate(update));
    if (awaitBackend) {
      await backendAction;
    }
    return agent;
  },
  async deleteAgent(agent, options = {}) {
    const { awaitBackend = false } = options;

    const customUserPropertyDuids = this.getPropertyByKind(PropertyKind.USER).map((e) => e.duid);
    const userPropertyDuids = customUserPropertyDuids.concat([
      this.defaultAssigneesProperty.duid,
      this.defaultReviewersProperty.duid,
      this.defaultUpdatedByProperty.duid,
    ]);

    if (customUserPropertyDuids.length > 0) {
      this.$cleanupTasksPropertyValuesNoBackend([agent.duid], customUserPropertyDuids);
    }
    this.$cleanupFormFieldsDefaultsNoBackend([agent.duid], userPropertyDuids);
    this.$cleanupLayoutsFiltersNoBackend([agent.duid], userPropertyDuids);

    this.$deleteUser(agent);

    const backendAction = this.$backend.user.update({ duid: agent.duid, status: UserStatus.DEACTIVATED });
    if (awaitBackend) {
      await backendAction;
    }
  },
  $setUsers(users) {
    setObjectMapAndSorted(this._duidsToUsers, this._sortedUserDuids, users, userComparator);
  },
  $createOrUpdateUser(user) {
    const userStore = this.$useUserStore();

    const currentUser = this.getUserByDuid(user.duid);

    if (!currentUser) {
      this._duidsToUsers.set(user.duid, user);
    } else {
      Object.assign(currentUser, user);
    }
    updateInSorted(this._duidsToUsers, this._sortedUserDuids, user.duid, userComparator);

    if (user.duid === userStore.duid) {
      userStore.setUser({ ...user, viewMode: userStore.viewMode });
    }
  },
  $deleteUser(user) {
    const userStore = this.$useUserStore();

    if (user.duid === userStore.duid) {
      userStore.forceLogout();
      return;
    }

    this._duidsToUsers.delete(user.duid);
    removeFromSorted(this._sortedUserDuids, user.duid);
  },
  $updateAgentStatusNoBackend(agent) {
    if (agent.status === UserStatus.AGENT_DISABLED || agent.status === UserStatus.DEACTIVATED) {
      return;
    }
    Object.assign(agent, {
      status: isAgentSetupRequired(agent) ? UserStatus.AGENT_CONFIG_ERROR : UserStatus.ACTIVE,
    });
  },
};

export { actions, getters };
